// @ts-nocheck
/* remove this rule @ts-nocheck, if you are working in this file, it was added to allow tests to run.
 but for proper development, type checks are necessary.
 also be aware that you will need to add the rule again if you commit changes to the file. */
import * as sjcl from 'sjcl';

export type SJCLBitArray = number[];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isSJCLBitArray = (value: any[]): value is SJCLBitArray => {
  return Array.isArray(value) && value.every((item) => typeof item === 'number');
};

/**
 * Converts an SJCL bit array to an ArrayBuffer with optional padding.
 *
 * Function from SJCL internal.
 *
 * @param {SJCLBitArray} arr - The input bit array to convert.
 * @returns {ArrayBuffer} - The resulting ArrayBuffer after conversion.
 * @throws {sjcl.exception.invalid} - If the bit size of the input array is not divisible by 8.
 */
export const fromBitsToArrayBuffer: (arr: SJCLBitArray) => ArrayBuffer = (arr: SJCLBitArray) => {
  const padding = true;
  const padding_count = 8;

  if (arr.length === 0) {
    return new ArrayBuffer(0);
  }

  let ol = sjcl.bitArray.bitLength(arr) / 8;

  //check to make sure the bitLength is divisible by 8, if it isn't
  //we can't do anything since arraybuffers work with bytes, not bits
  if (sjcl.bitArray.bitLength(arr) % 8 !== 0) {
    throw new sjcl.exception.invalid('Invalid bit size, must be divisble by 8 to fit in an arraybuffer correctly');
  }

  if (padding && ol % padding_count !== 0) {
    ol += padding_count - (ol % padding_count);
  }

  //padded temp for easy copying
  const tmp = new DataView(new ArrayBuffer(arr.length * 4));
  let i = 0;
  for (i = 0; i < arr.length; i++) {
    tmp.setUint32(i * 4, arr[i] << 32); //get rid of the higher bits
  }

  //now copy the final message if we are not going to 0 pad
  const out = new DataView(new ArrayBuffer(ol));

  //save a step when the tmp and out bytelength are ===
  if (out.byteLength === tmp.byteLength) {
    return tmp.buffer;
  }

  const smallest = tmp.byteLength < out.byteLength ? tmp.byteLength : out.byteLength;
  for (i = 0; i < smallest; i++) {
    out.setUint8(i, tmp.getUint8(i));
  }

  return out.buffer;
};

/**
 * Converts an ArrayBuffer to an SJCLBitArray.
 *
 * Function from SJCL internal.
 *
 * @param {ArrayBuffer} buffer - The ArrayBuffer to convert.
 * @return {SJCLBitArray} - The resulting bit array as per the SJCL library's format.
 */
export const fromArrayBufferToBits: (buffer: ArrayBuffer) => SJCLBitArray = (buffer: ArrayBuffer) => {
  const out: number[] = [];
  let inView: DataView;

  if (buffer.byteLength === 0) {
    return [];
  }

  if (buffer instanceof Uint8Array || buffer instanceof Uint16Array) {
    inView = new DataView(buffer.buffer);
  } else {
    inView = new DataView(buffer);
  }

  const len = inView.byteLength - (inView.byteLength % 4);

  for (let i = 0; i < len; i += 4) {
    out.push(inView.getUint32(i));
  }

  if (inView.byteLength % 4 != 0) {
    const tmp = new DataView(new ArrayBuffer(4));
    for (let i = 0, l = inView.byteLength % 4; i < l; i++) {
      //we want the data to the right, because partial slices off the starting bits
      tmp.setUint8(i + 4 - l, inView.getUint8(len + i)); // big-endian,
    }
    out.push(sjcl.bitArray.partial((inView.byteLength % 4) * 8, tmp.getUint32(0)));
  }
  return out as SJCLBitArray;
};
